import type { FC } from 'react';

import { ConsumptionResponse, ConsumptionTypeDto, TimeFrameDto } from '@eneco/dc-be/client';
import { useFormatter } from '@eneco/i18n';
import { Box, Card, Divider, Expandable, Heading, Stack, Text } from '@eneco/sparky';
import { useContent } from '@sitecore/common';
import { ConsumptionGraphicalOverviewRendering } from '@sitecore/types/ConsumptionGraphicalOverview';

import { useSelectedPeriod } from '../hooks/useSelectedPeriod';
import { electricity, gas, injection } from '../utils/colorMappings';

const LegendIcon = ({ color }: { color: string }) => {
  // eslint-disable-next-line dxp-rules/no-inline-css
  return <div style={{ width: 16, height: 16, borderRadius: '100%', backgroundColor: color }} />;
};

const ConsumptionGraphicalOverviewLegend: FC<{
  consumptionData: ConsumptionResponse | null;
  timeFrame: TimeFrameDto;
  consumptionType: Exclude<ConsumptionTypeDto, 'PeakValues'>;
}> = ({ consumptionData, timeFrame, consumptionType }) => {
  const { selected } = useSelectedPeriod(consumptionData);
  const { fields } = useContent<ConsumptionGraphicalOverviewRendering>();
  const { date: dateFormatter, unit } = useFormatter();

  const selectedPeriod =
    timeFrame === 'FifteenMin'
      ? consumptionData?.periods?.find(period => period.startDate === selected)
      : consumptionData?.periods?.find(period => period.startDate?.split('T')[0] === selected?.split('T')[0]);

  if (!selected) {
    return null;
  }

  const periodMapping = new Map<string, { label: string; color: string; value: string }>();

  const electricityConsumption =
    selectedPeriod?.data
      ?.filter(data => data.energyType === 'Electricity' && data.direction === 'OffTake')
      .reduce((sum, data) => sum + (data.value ?? 0), 0) ?? 0;
  const gasConsumption =
    selectedPeriod?.data?.filter(data => data.energyType === 'Gas').reduce((sum, data) => sum + (data.value ?? 0), 0) ??
    0;
  const injectionConsumption =
    selectedPeriod?.data
      ?.filter(data => data.energyType === 'Electricity' && data.direction === 'Injection')
      .reduce((sum, data) => sum + (data.value ?? 0), 0) ?? 0;

  if (electricityConsumption > 0) {
    periodMapping.set('electricity', {
      label: fields.legend.electricityLabel.value,
      color: electricity,
      value: unit.electricity(electricityConsumption),
    });
  }
  if (gasConsumption > 0) {
    periodMapping.set('gas', {
      label: fields.legend.gasLabel?.value ?? 'Gas',
      color: gas,
      value: unit.electricity(gasConsumption), // this is gas but we show kWh
    });
  }
  if (injectionConsumption > 0) {
    const value =
      consumptionType === 'Injection'
        ? unit.electricity(injectionConsumption)
        : `-${unit.electricity(injectionConsumption)}`;
    periodMapping.set('injection', {
      label: fields.legend.injectionLabel?.value,
      color: injection,
      value,
    });
  }

  let title = '';
  switch (timeFrame) {
    case 'Yearly':
      title = `${dateFormatter.year(selected)}`;
      break;
    case 'Daily':
    case 'Hourly':
    case 'FifteenMin':
      title = `${dateFormatter.hourLong(selected)} - ${dateFormatter.short(selected)}`;
      break;
    default:
      title = `${dateFormatter.monthLong(selected)} ${dateFormatter.year(selected)}`;
      break;
  }

  return (
    <Card>
      <Box paddingY="4" paddingX="6">
        <Expandable defaultOpen>
          <Stack gap="3">
            <Expandable.Trigger label={'Text'}>
              <Stack>
                <Heading as="h3" size={'3XS'}>
                  {/* eslint-disable-next-line dxp-rules/no-inline-css */}
                  <span style={{ textTransform: 'capitalize' }}>{title}</span>
                </Heading>
                <Text weight={'bold'} color={'textLowEmphasis'}>
                  {fields.legend.title.value ?? 'Totaalverbruik'}
                </Text>
              </Stack>
            </Expandable.Trigger>
            <Expandable.Content>
              <Stack gap="3">
                <Text weight={'bold'} size={'BodyS'}>
                  {fields.legend.totalLabel.value ?? 'Totaal'}
                </Text>
                <Divider />
                {Array.from(periodMapping.values()).map(period => (
                  <Stack direction={'row'} gap="3" alignY={'center'}>
                    <Stack.Item shrink={false}>
                      <LegendIcon color={period.color} />
                    </Stack.Item>
                    <Text size={'BodyS'}>{period.label}</Text>
                    <Text size={'BodyS'} color={'textLowEmphasis'}>
                      ({period.value})
                    </Text>
                  </Stack>
                ))}
              </Stack>
            </Expandable.Content>
          </Stack>
        </Expandable>
      </Box>
    </Card>
  );
};

export default ConsumptionGraphicalOverviewLegend;
